import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Switch,
  Text
} from '@chakra-ui/react'
import { memo, useEffect, useRef } from 'react'

import { FormField } from '../../../../../components/FormField'
import { formatIsoDate, slugify, WEBSITE_URL } from '../../../../../helpers'
import { EventType, SeriesOverrideGroup } from '../../../../../services/Event/types'
import { SelectTeacher } from '../../../../../services/Teacher/types'
import { GeneralFieldsEditor, needsLocation } from '../GeneralFieldsEditor'
import { ProfessionalsEditor } from '../ProfessionalsEditor'
import { ALL_GROUPS, EditionForm, isGeneralGroup, OVERRIDE_GROUP_LABELS, SeriesForm } from './eventSeriesFormShared'

interface IProps {
  edition: EditionForm
  index: number
  /** Whether removing this edition should be allowed (mirrors the parent's guard). */
  canRemove: boolean
  fieldErrors: Record<string, string[]>
  saving: boolean
  teachers: SelectTeacher[]
  eventTypes: EventType[]
  activeCampaignLists: { id: string; name: string }[]
  seriesFormat: SeriesForm['format']
  removeEdition: (key: string) => void
  patchEdition: (key: string, patch: Partial<EditionForm>) => void
  toggleOverride: (edition: EditionForm, group: SeriesOverrideGroup, enabled: boolean) => void
  // Precomputed by the parent (rather than passed as functions) so this component's
  // React.memo can rely on plain value equality — a function prop would be a new
  // reference every parent render regardless of whether anything this card cares
  // about actually changed, defeating the memoization.
  targetSlugValue: string
  willChangeSlugValue: boolean
  editionLabelValue: string
  helpFormAllowedValue: boolean
  /** Scrolls this card into view on mount — set for the one edition the "Editar
   *  edição" menu item was clicked on. */
  focusOnMount?: boolean
}

/** Number of field errors sitting under this edition's `editions.{index}.` prefix. */
function editionErrorCount(fieldErrors: Record<string, string[]>, prefix: string): number {
  return Object.entries(fieldErrors).filter(
    ([key, value]) => key.startsWith(prefix) && (Array.isArray(value) ? value.length > 0 : Boolean(value))
  ).length
}

/**
 * One edition's card, as a collapsed-by-default accordion item. Memoized so typing in
 * one edition doesn't re-render every other one — the heavy content (professionals'
 * rich-text bios, and any active override's GeneralFieldsEditor) only mounts once the
 * card is actually expanded, gated on `isExpanded` rather than relying on Accordion's
 * own Collapse (which keeps collapsed children mounted, just visually hidden).
 */
export const EditionCard = memo(function EditionCard({
  edition,
  index,
  canRemove,
  fieldErrors,
  saving,
  teachers,
  eventTypes,
  activeCampaignLists,
  seriesFormat,
  removeEdition,
  patchEdition,
  toggleOverride,
  targetSlugValue,
  willChangeSlugValue,
  editionLabelValue,
  helpFormAllowedValue,
  focusOnMount
}: IProps) {
  const effectiveFormat = edition.overrides.includes('format') ? edition.format : seriesFormat
  const prefix = `editions.${index}.`
  const customSlug = edition.overrides.includes('slug')
  // `slug` has its own field above, so it must not open the general block.
  const generalOverrides = edition.overrides.filter(isGeneralGroup)
  const errorCount = editionErrorCount(fieldErrors, prefix)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (focusOnMount) cardRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    // Mount-only: the modal remounts fresh (new `key`) every time it's opened, so
    // `focusOnMount` never changes after that for a given card instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box ref={cardRef}>
      <AccordionItem border="1px solid" borderColor="gray.300" borderRadius="md" overflow="hidden">
        {({ isExpanded }) => (
          <>
            <Flex alignItems="center">
              <AccordionButton flex="1" py={3}>
                <Flex flex="1" alignItems="center" gap={2} textAlign="left" wrap="wrap">
                  <Text fontWeight="bold">{editionLabelValue}</Text>
                  {edition.date_start && <Text variant="caption">{formatIsoDate(edition.date_start)}</Text>}
                  {errorCount > 0 && <Badge colorScheme="red">{errorCount}</Badge>}
                </Flex>
                <AccordionIcon />
              </AccordionButton>
              {/* Outside AccordionButton (which renders as a <button>) so this isn't a
            nested button — clicking it removes the edition without toggling the panel. */}
              {canRemove && (
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="ghost"
                  mx={2}
                  onClick={() => removeEdition(edition._key)}
                  disabled={saving}
                >
                  Remover edição
                </Button>
              )}
            </Flex>

            <AccordionPanel pb={4} borderTopWidth="1px" borderColor="gray.200">
              {isExpanded && (
                <Flex direction="column" gap={3} pt={1}>
                  <Flex gap={3} wrap="wrap">
                    <FormField
                      label="Nome da edição"
                      error={fieldErrors[`${prefix}name`]?.[0]}
                      isRequired
                      flex="1"
                      minW="200px"
                    >
                      <Input
                        size="sm"
                        value={edition.name}
                        onChange={(e) => patchEdition(edition._key, { name: e.target.value })}
                        disabled={saving}
                      />
                    </FormField>
                    <FormControl flex="1.5" minW="260px" isInvalid={!!fieldErrors[`${prefix}slug`]}>
                      <FormLabel fontSize="sm">URL (slug) {customSlug && '*'}</FormLabel>
                      <InputGroup size="sm">
                        <InputLeftAddon>{`${WEBSITE_URL}/eventos/`}</InputLeftAddon>
                        <Input
                          value={customSlug ? edition.slug : targetSlugValue}
                          onChange={(e) => {
                            const slug = slugify(e.target.value)
                            // Mirror the typed URL into the draft so toggling the tag
                            // off and back on restores it instead of the automatic one.
                            patchEdition(edition._key, { slug, customSlugDraft: slug })
                          }}
                          // Read-only instead of disabled: the derived URL stays focusable,
                          // selectable/copiable and visible to screen readers. The greyed
                          // background keeps the "you can't type here" cue Chakra drops.
                          isReadOnly={!customSlug}
                          bg={!customSlug ? 'gray.50' : undefined}
                          disabled={saving}
                          placeholder={customSlug ? 'minha-edicao-2026' : 'Defina a URL da série acima'}
                        />
                      </InputGroup>
                      {!customSlug && (
                        <FormHelperText fontSize="xs">
                          Gerada automaticamente a partir da URL da série. Para personalizá-la, ative a tag “URL”
                          abaixo.
                        </FormHelperText>
                      )}
                      {willChangeSlugValue && (
                        <Alert status="warning" fontSize="xs" mt={1} borderRadius="md" py={1.5}>
                          <AlertIcon />
                          <Text>
                            A URL pública desta edição mudará de <b>/{edition.publishedSlug}</b> para{' '}
                            <b>/{targetSlugValue}</b> ao salvar. Links já divulgados deixarão de funcionar.
                          </Text>
                        </Alert>
                      )}
                      <FormErrorMessage>{fieldErrors[`${prefix}slug`]?.[0]}</FormErrorMessage>
                    </FormControl>
                  </Flex>

                  <Flex gap={6} alignItems="center">
                    <Flex alignItems="center" gap={2}>
                      <FormField label="Visível no site" mb={0}>
                        <Switch
                          isChecked={edition.visible}
                          onChange={(e) => patchEdition(edition._key, { visible: e.target.checked })}
                          isDisabled={saving}
                        />
                      </FormField>
                    </Flex>
                    <Flex alignItems="center" gap={2}>
                      <FormField label="Check-in NFC" mb={0}>
                        <Switch
                          isChecked={edition.use_nfc}
                          onChange={(e) => patchEdition(edition._key, { use_nfc: e.target.checked })}
                          isDisabled={saving}
                        />
                      </FormField>
                    </Flex>
                  </Flex>

                  <Flex gap={3} wrap="wrap">
                    <FormField
                      label="Data"
                      error={fieldErrors[`${prefix}date_start`]?.[0]}
                      isRequired
                      flex="1"
                      minW="150px"
                    >
                      <Input
                        size="sm"
                        type="date"
                        value={edition.date_start}
                        onChange={(e) => patchEdition(edition._key, { date_start: e.target.value })}
                        disabled={saving}
                      />
                    </FormField>
                    <Box flex="1" minW="120px" display="flex" alignItems="flex-end" pb={2} gap={2}>
                      <FormField label="Mais de um dia?" mb={0}>
                        <Switch
                          isChecked={edition.is_multiday}
                          onChange={(e) => patchEdition(edition._key, { is_multiday: e.target.checked, date_end: '' })}
                          isDisabled={saving}
                        />
                      </FormField>
                    </Box>
                    {edition.is_multiday && (
                      <FormField
                        label="Data de Término"
                        error={fieldErrors[`${prefix}date_end`]?.[0]}
                        isRequired
                        flex="1"
                        minW="150px"
                      >
                        <Input
                          size="sm"
                          type="date"
                          value={edition.date_end}
                          min={edition.date_start}
                          onChange={(e) => patchEdition(edition._key, { date_end: e.target.value })}
                          disabled={saving}
                        />
                      </FormField>
                    )}
                  </Flex>

                  <Flex gap={3}>
                    <FormField
                      label="Horário de Início"
                      error={fieldErrors[`${prefix}time_start`]?.[0]}
                      isRequired
                      flex="1"
                    >
                      <Input
                        size="sm"
                        type="time"
                        value={edition.time_start}
                        onChange={(e) => patchEdition(edition._key, { time_start: e.target.value })}
                        disabled={saving}
                      />
                    </FormField>
                    <FormField
                      label="Horário de Término"
                      error={fieldErrors[`${prefix}time_end`]?.[0]}
                      isRequired
                      flex="1"
                    >
                      <Input
                        size="sm"
                        type="time"
                        value={edition.time_end}
                        onChange={(e) => patchEdition(edition._key, { time_end: e.target.value })}
                        disabled={saving}
                      />
                    </FormField>
                  </Flex>

                  <Box>
                    <FormField label="Profissionais">
                      <ProfessionalsEditor
                        professionals={edition.professionals}
                        onChange={(next) => patchEdition(edition._key, { professionals: next })}
                        teachers={teachers}
                        disabled={saving}
                        fieldErrors={fieldErrors}
                        errorPrefix={`${prefix}professionals.`}
                      />
                    </FormField>
                  </Box>

                  <Divider />
                  <Text fontWeight="600" fontSize="sm">
                    Personalizar configurações desta edição
                  </Text>
                  <Text variant="caption">Ative um item para sobrescrever o padrão da série apenas nesta edição.</Text>
                  <Flex gap={2} wrap="wrap">
                    {ALL_GROUPS.map((group) => {
                      const active = edition.overrides.includes(group)
                      return (
                        <Badge
                          key={group}
                          as="button"
                          type="button"
                          onClick={() => toggleOverride(edition, group, !active)}
                          colorScheme={active ? 'magenta' : 'gray'}
                          variant={active ? 'solid' : 'outline'}
                          px={2}
                          py={1}
                          borderRadius="md"
                          cursor="pointer"
                        >
                          {active ? '✓ ' : '+ '}
                          {OVERRIDE_GROUP_LABELS[group]}
                        </Badge>
                      )
                    })}
                  </Flex>

                  <Collapse in={generalOverrides.length > 0} animateOpacity>
                    <Box border="1px dashed" borderColor="magenta.200" borderRadius="md" p={3} mt={2}>
                      <GeneralFieldsEditor
                        value={edition}
                        onChange={(patch) => patchEdition(edition._key, patch)}
                        groups={generalOverrides}
                        eventTypes={eventTypes}
                        activeCampaignLists={activeCampaignLists}
                        formatForLocation={effectiveFormat}
                        disabled={saving}
                        helpFormAllowed={helpFormAllowedValue}
                        errors={fieldErrors}
                        errorPrefix={prefix}
                      />
                      {edition.overrides.includes('location') && !needsLocation(effectiveFormat) && (
                        <Text variant="caption">O local só se aplica a edições presenciais ou híbridas.</Text>
                      )}
                    </Box>
                  </Collapse>
                </Flex>
              )}
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Box>
  )
})
